import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import {
  LAND_MATCH_PATTERN,
  LAND_RESULT_CONTENTS,
  ResultContent,
} from "./data";

// Site::Match::Question::Component の Stimulus コントローラ。
// landIeul: components/Match/Question/index.tsx の 選択→診断→結果表示 を再現。
// 選択で activeChoiceBtn をトグル、3問揃うと診断ボタン活性化、診断で結果 (QuestionResult) を描画してスクロール。

interface PrefGroup {
  name: string;
  items: { id: string; name: string }[];
}

class SiteMatchQuestionController extends Controller {
  static targets = ["startButton"];
  static values = { graphql: String, prefectures: Array };

  declare readonly startButtonTarget: HTMLButtonElement;
  declare readonly graphqlValue: string;
  declare readonly prefecturesValue: PrefGroup[];

  answers: Record<number, string> = { 1: "", 2: "", 3: "" };
  rendered = false;

  // #scrollTarget は controller 要素 (.Question_container) の外にあるため id で取得する。
  private get scrollTargetEl(): HTMLElement | null {
    return document.getElementById("scrollTarget");
  }

  // 選択肢クリック: 同じ設問の選択を解除して当該を active に
  choose(event: ActionEvent): void {
    const input = event.currentTarget as HTMLInputElement;
    const parent = input.parentElement;
    if (!parent) return;
    const groupClass = parent.classList[0]; // choiseItem1 など
    this.element
      .querySelectorAll(`.${groupClass}`)
      .forEach((el) => el.classList.remove("Question_activeChoiceBtn"));
    parent.classList.add("Question_activeChoiceBtn");

    const order = Number(event.params.order);
    this.answers[order] = String(event.params.value);
    this.updateButton();
  }

  private updateButton(): void {
    const filled = this.answers[1] && this.answers[2] && this.answers[3];
    this.startButtonTarget.disabled = !filled;
  }

  // 診断する
  diagnose(event: Event): void {
    event.preventDefault();
    const answer = [this.answers[1], this.answers[2], this.answers[3]];
    const target = LAND_MATCH_PATTERN.find(
      (obj) => JSON.stringify(answer) === JSON.stringify(obj.inputPattern),
    );
    if (!target) return;
    const results = target.resultPattern
      .map((id) => LAND_RESULT_CONTENTS.find((r) => r.id === id))
      .filter((r): r is ResultContent => !!r);
    if (results.length === 0) return;

    this.renderResult(results);
    this.scrollToResult();
  }

  private renderResult(results: ResultContent[]): void {
    // 既存の結果を差し替え
    const existing = document.getElementById("matchQuestionResult");
    if (existing) existing.remove();

    const wrap = document.createElement("div");
    wrap.id = "matchQuestionResult";
    wrap.innerHTML = this.resultHtml(results);
    const anchor = this.scrollTargetEl;
    if (anchor) anchor.insertAdjacentElement("afterend", wrap);
    this.rendered = true;
  }

  private resultHtml(results: ResultContent[]): string {
    const appeal = results
      .map((r, i) => `<p><span>第${i + 1}位</span>${r.name}</p>`)
      .join("");
    const boxes = results
      .map(
        (r, i) => `
        <div class="QuestionResult_resultBox">
          <span class="QuestionResult_resultNumber">第${i + 1}位</span>
          <p class="QuestionResult_resultTitle">${r.name}</p>
          <div class="QuestionResult_resultText">${r.content}</div>
        </div>`,
      )
      .join("");

    return `
      <h3 class="QuestionResult_title">あなたにおすすめの活用方法</h3>
      <div class="QuestionResult_appealResult">${appeal}</div>
      <div class="QuestionResult_container">
        <div class="QuestionResult_content">
          ${boxes}
          <div style="position:relative;display:flex;flex-direction:column;align-items:center;background:#f0f0f0;padding-top:40px;padding-bottom:0;font-size:16px;">
            ${this.problemHeadHtml()}
          </div>
        </div>
        ${this.ctaHtml()}
      </div>`;
  }

  private problemHeadHtml(): string {
    const isSp = window.innerWidth <= 768;
    const circlesOrImg = isSp
      ? `<img class="ProblemHead_img" src="/lp02to04/section03_illust01_sp.png" alt="問題画像">`
      : `<div class="ProblemHead_circle">資金がいくら<br>必要かわからない。<br>そんなに資金ないけど<br>大丈夫？</div>
         <div class="ProblemHead_circle">相談できる人が<br>いない…</div>
         <div class="ProblemHead_circle">失敗したら<br>どうしよう…</div>
         <div class="ProblemHead_circle">何をすればいいか<br>わからない。<br>節税効果って<br>本当にあるの？</div>`;
    const midashi = isSp
      ? "/lp02to04/section03_midashi_sp.png"
      : "/lp02to04/section03_midashi_pc.png";
    const text = isSp
      ? `<p>まず実績のある専門業者に</p><p>相談するところから始めてみてください。</p><p>イエウール土地活用の『土地活用プラン請求』で</p><p>あれば、あなたの立地に最適な間取り・企画・</p><p>運営方法、更には収益シュミレーションなどの</p><p>プランを複数社の優良企業からご提案します！</p>`
      : `<p>まず実績のある専門業者に相談するところから始めてみてください。</p><p>イエウール土地活用の『土地活用プラン請求』であれば、あなたの立地に最適な間取り・企画・運営方法、</p><p>更には収益シュミレーションなどのプランを複数社の優良企業からご提案します！<br></p>`;

    return `
      <div class="ProblemHead_contents">
        <img class="ProblemHead_headtext" src="${midashi}" alt="問題見出し">
        <div class="ProblemHead_headcontentsContent">${circlesOrImg}</div>
        <div class="ProblemHead_headcontents">
          <div class="ProblemHead_text">
            ${text}
            <p class="ProblemHead_emphasisText">厳選した複数企業からあなたの希望や状況に合った理想的なプランを提案してもらうことで</p>
            <p class="ProblemHead_emphasisTextYellow">あなたにぴったりの土地活用を実現しましょう。</p>
          </div>
        </div>
      </div>`;
  }

  private prefectureOptions(): string {
    let html = '<option value="">都道府県</option>';
    this.prefecturesValue.forEach((g) => {
      html += `<optgroup label="${g.name}">`;
      g.items.forEach((p) => {
        html += `<option value="${p.id}">${p.name}</option>`;
      });
      html += "</optgroup>";
    });
    return html;
  }

  private ctaHtml(): string {
    const fontSize = window.innerWidth <= 414 ? 16 : 22;
    return `
      <div class="MatchResultCta_areaCta" data-controller="site--match--result-cta" data-site--match--result-cta-graphql-value="${this.graphqlValue}">
        <p style="font-size:${fontSize}px">＼最適な土地活用プランって？／</p>
        <form>
          <div class="MatchResultCta_inner">
            <div class="MatchResultCta_catch">
              <h2 class="MatchResultCta_ctaTitle">大手10社の収益プランを比較する</h2>
            </div>
            <p class="MatchResultCta_noticeNoLand" data-site--match--result-cta-target="notice" style="display:none">※土地からお探しの方は、まずはご希望のエリア、または現住所をご入力いただければ、最適なプランをご紹介します。</p>
            <div class="MatchResultCta_formGroup">
              <div class="MatchResultCta_selectGroups" data-site--match--result-cta-target="group" data-level="possession">
                <div class="MatchResultCta_selectForm" data-site--match--result-cta-target="form">
                  <select data-level="possession" data-site--match--result-cta-target="possession" data-action="site--match--result-cta#change">
                    <option value="">土地の有無</option><option value="yes">有り</option><option value="no">無し</option>
                  </select>
                </div>
              </div>
              <div class="MatchResultCta_selectGroups" data-site--match--result-cta-target="group" data-level="prefecture">
                <div class="MatchResultCta_selectForm" data-site--match--result-cta-target="form">
                  <select data-level="prefecture" data-site--match--result-cta-target="prefecture" data-action="site--match--result-cta#change">${this.prefectureOptions()}</select>
                </div>
              </div>
              <div class="MatchResultCta_selectGroups" data-site--match--result-cta-target="group" data-level="city">
                <div class="MatchResultCta_selectForm" data-site--match--result-cta-target="form">
                  <select data-level="city" data-site--match--result-cta-target="city" data-action="site--match--result-cta#change"><option value="">市区町村</option></select>
                </div>
              </div>
              <div class="MatchResultCta_selectGroups" data-site--match--result-cta-target="group" data-level="town">
                <div class="MatchResultCta_selectForm" data-site--match--result-cta-target="form">
                  <select data-level="town" data-site--match--result-cta-target="town" data-action="site--match--result-cta#change"><option value="">町名</option></select>
                </div>
              </div>
              <div class="MatchResultCta_selectGroups" data-site--match--result-cta-target="group" data-level="street">
                <div class="MatchResultCta_selectForm" data-site--match--result-cta-target="form">
                  <select data-level="street" data-site--match--result-cta-target="street" data-action="site--match--result-cta#change"><option value="">字・丁目</option><optgroup label="その他"><option value="0">その他</option></optgroup></select>
                </div>
              </div>
            </div>
            <a class="MatchResultCta_areaCtaLink" data-site--match--result-cta-target="link" target="_blank" rel="nofollow" href="/plan-request/?property-type-id=land_usage_&pref-id=&city-id=&town-id=&prefecture_name=&city_name=&town_name=&street-value=">
              <div class="MatchResultCta_areaCtaBtn" id="lpAreaCtaBtn"></div>
            </a>
          </div>
        </form>
      </div>`;
  }

  private scrollToResult(): void {
    const anchor = this.scrollTargetEl;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const top = window.pageYOffset + rect.top;
    window.scrollTo({ top, behavior: "smooth" });
  }
}

interface ActionEvent extends Event {
  params: { order?: string; value?: string };
  currentTarget: EventTarget & HTMLElement;
}

application.register("site--match--question", SiteMatchQuestionController);

// ---------------------------------------------------------------------------
// 診断結果 CTA (ClickToAction) の 土地の有無→都道府県→市区町村→町名→字丁目 の段階選択。
// landIeul: components/Match/QuestionResult/ClickToAction/index.tsx。動的挿入されるため別コントローラで接続する。
// 市区町村以下は GraphQL (GetCities/GetTowns/GetStreets) で取得し yomi で optgroup 化する (home ClickToAction と同一)。
interface NamedItem {
  id: string;
  name: string;
  yomi: string;
}

const GROUP_MAP: [RegExp, string][] = [
  [/^[ア-オ]/, "ア"],
  [/^[カ-コガ-ゴ]/, "カ"],
  [/^[サ-ソザ-ゾ]/, "サ"],
  [/^[タ-トダ-ド]/, "タ"],
  [/^[ナ-ノ]/, "ナ"],
  [/^[ハ-ホバ-ボパ-ポ]/, "ハ"],
  [/^[マ-モ]/, "マ"],
  [/^[ヤ-ヨ]/, "ヤ"],
  [/^[ラ-ロ]/, "ラ"],
  [/^[ワ-ン]/, "ワ"],
];

function mapToGroup(yomi: string): string {
  const m = GROUP_MAP.find(([re]) => re.test(yomi));
  return m ? m[1] : "その他";
}

function groupByYomi(items: NamedItem[]): { name: string; items: NamedItem[] }[] {
  const map = new Map<string, NamedItem[]>();
  items.forEach((it) => {
    const g = mapToGroup(it.yomi);
    map.set(g, [...(map.get(g) || []), it]);
  });
  return Array.from(map.keys())
    .sort()
    .map((k) => ({
      name: k,
      items: (map.get(k) || []).sort((a, b) =>
        a.yomi < b.yomi ? -1 : a.yomi > b.yomi ? 1 : 0,
      ),
    }));
}

const GET_CITIES = `query GetCities($id: ID!){prefecture(id:$id){cities{id name yomi}}}`;
const GET_TOWNS = `query GetTowns($id: ID!){city(id:$id){towns{id name yomi}}}`;
const GET_STREETS = `query GetStreets($id: ID!){town(id:$id){streets{id name yomi}}}`;

const CURRENT = "MatchResultCta_current";
const DONE = "MatchResultCta_done";

class SiteMatchResultCtaController extends Controller {
  static targets = [
    "possession",
    "prefecture",
    "city",
    "town",
    "street",
    "group",
    "form",
    "link",
    "notice",
  ];
  static values = { graphql: String };

  declare readonly possessionTarget: HTMLSelectElement;
  declare readonly prefectureTarget: HTMLSelectElement;
  declare readonly cityTarget: HTMLSelectElement;
  declare readonly townTarget: HTMLSelectElement;
  declare readonly streetTarget: HTMLSelectElement;
  declare readonly groupTargets: HTMLElement[];
  declare readonly formTargets: HTMLElement[];
  declare readonly linkTarget: HTMLAnchorElement;
  declare readonly noticeTarget: HTMLElement;
  declare readonly graphqlValue: string;

  private readonly LEVELS = ["possession", "prefecture", "city", "town", "street"];

  connect(): void {
    this.refresh();
  }

  async change(event: Event): Promise<void> {
    const changed = (event.target as HTMLElement).dataset.level || "possession";

    if (changed === "possession") {
      if (this.possessionTarget.value === "") this.resetFrom("prefecture");
      this.noticeTarget.style.display =
        this.possessionTarget.value === "no" ? "" : "none";
    } else if (changed === "prefecture") {
      await this.reloadCities();
    } else if (changed === "city") {
      await this.reloadTowns();
    } else if (changed === "town") {
      await this.reloadStreets();
    }
    this.refresh();
  }

  private async fetchData(query: string, id: string): Promise<any> {
    const res = await fetch(this.graphqlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, variables: { id } }),
    });
    const json = await res.json();
    return json.data;
  }

  private optionsHtml(placeholder: string, groups: { name: string; items: NamedItem[] }[]): string {
    let html = `<option value="">${placeholder}</option>`;
    groups.forEach((g) => {
      html += `<optgroup label="${g.name}">`;
      g.items.forEach((it) => {
        html += `<option value="${it.id}">${it.name}</option>`;
      });
      html += "</optgroup>";
    });
    return html;
  }

  private async reloadCities(): Promise<void> {
    this.resetFrom("city");
    const pref = this.prefectureTarget.value;
    if (!pref) return;
    const data = await this.fetchData(GET_CITIES, pref);
    const cities: NamedItem[] = data?.prefecture?.cities || [];
    this.cityTarget.innerHTML = this.optionsHtml("市区町村", groupByYomi(cities));
  }

  private async reloadTowns(): Promise<void> {
    this.resetFrom("town");
    const city = this.cityTarget.value;
    if (!city) return;
    const data = await this.fetchData(GET_TOWNS, city);
    const towns: NamedItem[] = data?.city?.towns || [];
    this.townTarget.innerHTML = this.optionsHtml("町名", groupByYomi(towns));
  }

  private async reloadStreets(): Promise<void> {
    const town = this.townTarget.value;
    if (!town) {
      this.resetStreet();
      return;
    }
    const data = await this.fetchData(GET_STREETS, town);
    let streets: NamedItem[] = data?.town?.streets || [];
    if (streets.length === 0) streets = [{ id: "0", name: "その他", yomi: "その他" }];
    this.streetTarget.innerHTML = this.optionsHtml("字・丁目", groupByYomi(streets));
  }

  private resetFrom(level: string): void {
    const order = ["prefecture", "city", "town", "street"];
    const start = order.indexOf(level);
    order.slice(start).forEach((l) => {
      if (l === "prefecture") this.prefectureTarget.value = "";
      if (l === "city") this.cityTarget.innerHTML = '<option value="">市区町村</option>';
      if (l === "town") this.townTarget.innerHTML = '<option value="">町名</option>';
      if (l === "street") this.resetStreet();
    });
  }

  private resetStreet(): void {
    this.streetTarget.innerHTML =
      '<option value="">字・丁目</option><optgroup label="その他"><option value="0">その他</option></optgroup>';
  }

  private refresh(): void {
    const vals = [
      this.possessionTarget.value,
      this.prefectureTarget.value,
      this.cityTarget.value,
      this.townTarget.value,
      this.streetTarget.value,
    ];
    const activeIdx = vals.findIndex((v) => !v);

    this.LEVELS.forEach((level, i) => {
      const filled = !!vals[i];
      const isCurrent = i === activeIdx;
      const group = this.groupTargets.find((g) => g.dataset.level === level);
      const form = group?.querySelector<HTMLElement>(
        `[data-site--match--result-cta-target="form"]`,
      );
      [group, form].forEach((el) => {
        if (!el) return;
        el.classList.toggle(DONE, filled);
        el.classList.toggle(CURRENT, isCurrent);
      });
    });

    const complete = activeIdx === -1;
    this.linkTarget.classList.toggle(CURRENT, complete);
    this.linkTarget.href = this.buildHref();
  }

  private buildHref(): string {
    const text = (sel: HTMLSelectElement, placeholder: string) => {
      const t = sel.selectedOptions[0]?.text || "";
      return t === placeholder ? "" : t;
    };
    const pos = this.possessionTarget.value;
    const pref = this.prefectureTarget.value;
    const city = this.cityTarget.value;
    const town = this.townTarget.value;
    const prefName = text(this.prefectureTarget, "都道府県");
    const cityName = text(this.cityTarget, "市区町村");
    const townName = text(this.townTarget, "町名");
    const streetName = text(this.streetTarget, "字・丁目");
    const base = `property-type-id=land_usage_${pos}&pref-id=${pref}&city-id=${city}&town-id=${town}&prefecture_name=${prefName}&city_name=${cityName}&town_name=${townName}&street-value=${streetName}`;
    const search = window.location.search;
    return search ? `/plan-request/${search}&${base}` : `/plan-request/?${base}`;
  }
}

application.register("site--match--result-cta", SiteMatchResultCtaController);
